import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { AlertTriangle, Info, XOctagon, Lightbulb } from 'lucide-react';

export interface CritiqueData {
  issues: Array<{
    id: string;
    severity: 'error' | 'warning' | 'info';
    code: string;
    message: string;
    partIds?: string[];
    jointIds?: string[];
  }>;
  suggestions: string[];
  ranAt?: string;
  model?: string;
}

interface Props {
  critique: CritiqueData | null | undefined;
  className?: string;
}

export function CritiqueDisplay({ critique, className }: Props) {
  const { t } = useTranslation('common');
  if (!critique) return null;
  if (critique.issues.length === 0 && critique.suggestions.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-md border bg-muted/30 p-3 space-y-2 text-sm',
        className,
      )}
    >
      <div className="text-xs font-semibold text-muted-foreground">
        {t('spec.visionEval')}
        {critique.model && (
          <span className="font-normal"> · {critique.model}</span>
        )}
      </div>
      {critique.issues.map((issue) => (
        <div key={issue.id} className="flex gap-2 items-start">
          <SeverityIcon severity={issue.severity} />
          <div className="flex-1 min-w-0">
            <div>{issue.message}</div>
            {((issue.partIds && issue.partIds.length > 0) ||
              (issue.jointIds && issue.jointIds.length > 0)) && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {issue.partIds?.length ? `${t('spec.partsLabel')}: ${issue.partIds.join(', ')}` : ''}
                {issue.partIds?.length && issue.jointIds?.length ? ' · ' : ''}
                {issue.jointIds?.length
                  ? `${t('spec.jointsLabel')}: ${issue.jointIds.join(', ')}`
                  : ''}
              </div>
            )}
          </div>
        </div>
      ))}
      {critique.suggestions.length > 0 && (
        <div className="pt-2 space-y-1 border-t">
          {critique.suggestions.map((s, i) => (
            <div key={i} className="flex gap-2 items-start">
              <Lightbulb className="w-4 h-4 text-brand-indigo shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">{s}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SeverityIcon({ severity }: { severity: 'error' | 'warning' | 'info' }) {
  if (severity === 'error')
    return <XOctagon className="w-4 h-4 text-brand-indigo shrink-0 mt-0.5" />;
  if (severity === 'warning')
    return <AlertTriangle className="w-4 h-4 text-brand-indigo shrink-0 mt-0.5" />;
  return <Info className="w-4 h-4 text-brand-indigo/60 shrink-0 mt-0.5" />;
}
